import {styled, css} from 'styled-components';
import React from 'react';
import {H2} from './headings';
import {CenteredColumnContainer} from './centered-column-container';
import {StyledProps, mediaQueries} from '../modules/common';
import {HeaderNoise, GradientType} from './header-noise';
import {AppScoreWidget} from './app-score-widget';
import {
    typographyHeading1Styles,
    typographyHeading2Styles,
    typographyHeading3Styles,
    typographyHeading4Styles,
} from '../modules/common/styles';
import ReviewsIoRatingBarWidget from './reviews-io/reviews-io-rating-bar-widget';
import {I18n} from '../modules/util/i18n/i18n-interface';
import withTranslation from 'next-translate/withTranslation';

interface HeaderProps extends StyledProps {
    i18n: I18n;
    title?: string;
    subtitle?: string[] | string;
    children?: React.ReactNode;
    gradient?: GradientType;
}

const CenteredColumContainerHeader = styled(CenteredColumnContainer)`
    align-self: center;
    position: relative;
    margin-top: 6.5rem;
    ${mediaQueries.lessThan<HeaderProps>('small')`         
        padding: 0 24px;
        ${css`
            margin-top: 4rem;
        `};
     `}
`;

const Spacer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: hidden;

    ${mediaQueries.greaterThan('small')`
        padding: 8rem 0 1.5rem 0;
    `}

    ${mediaQueries.lessThan('small')`
        padding: 0rem 0 1rem 0;
    `}
`;

const StyledH1 = styled.h1`
    margin: 0.5rem 0;
    text-align: center;
    padding: 0 1rem;
`;

const UnstyledHeader = (props: HeaderProps): JSX.Element => {
    const {className, title, subtitle, children, gradient} = props;
    const {lang} = props.i18n;

    return (
        <header className={className}>
            {gradient && <HeaderNoise gradient={gradient} noise />}
            <CenteredColumContainerHeader className="centeredColumContainerHeader">
                {title && <StyledH1> {title} </StyledH1>}
                {subtitle && (
                    <H2 style={{padding: '0 1rem', maxWidth: '960px'}}>
                        {Array.isArray(subtitle)
                            ? subtitle.map((item, i) => (
                                  <span key={i}>
                                      {item} <br />{' '}
                                  </span>
                              ))
                            : subtitle}
                    </H2>
                )}
                {children}
                <Spacer>
                    <AppScoreWidget appleScore={4.9} playScore={4.8} />
                    <ReviewsIoRatingBarWidget
                        lang={lang}
                        widgetId="WCzZgnHYaKOpD8xU"
                        storeName="www.fonoma.com"
                    />
                </Spacer>
            </CenteredColumContainerHeader>
        </header>
    );
};

const StyledHeader = styled(UnstyledHeader)`
    position: relative;
    display: flex;
    justify-content: center;

    background-size: auto 100%;
    background-position-x: center;
    min-height: calc(
        ${(props): string =>
                `${props.theme.desktopMeasures.headerHeight}${props.theme.unitsOfMeasure.px}`} -
            ${(props): string =>
                `${props.theme.desktopMeasures.navBarHeight}${props.theme.unitsOfMeasure.px}`}
    );

    h1,
    h2 {
        color: ${(props): string => props.theme.colors.ballena};
        margin: 8px 0;
    }

    h1 {
        ${typographyHeading1Styles}
        margin-bottom: ${(props): string => (props.subtitle ? '8px' : '40px')};

        ${mediaQueries.lessThan('small')`
            ${typographyHeading2Styles}
        `}
    }

    h2 {
        margin-bottom: 40px;
        ${typographyHeading3Styles}

        ${mediaQueries.lessThan('small')`
            ${typographyHeading4Styles}
        `}
    }

    button {
        ${mediaQueries.lessThan('small')`
            margin-bottom: 4rem;
        `}
    }

    > div {
        display: flex;
        justify-content: center;

        width: 100%;
        width: -moz-available;
        width: -webkit-fill-available;
        max-width: unset;
    }

    ${mediaQueries.lessThan<HeaderProps>('small')`
        background-size: auto 100%;
        height: unset;
        min-height: ${(props): string =>
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-unsafe-member-access
            `${props.theme.mobileMeasures.headerHeight}${props.theme.unitsOfMeasure.px}`};
     `}
`;

const Header = withTranslation(StyledHeader);

export {Header};
